import axios from 'axios';
import { getSiteConfig, getConfigValue } from 'utils/site';

// Configuración base del servicio
const getAPIConfig = () => {
  const siteConfig = JSON.parse(getSiteConfig() || '{}');
  const wpApiUrl = getConfigValue(siteConfig, 'WP_API_URL');
  
  if (!wpApiUrl) {
    throw new Error('WP_API_URL no está configurado');
  }

  return {
    baseURL: `${wpApiUrl}/datofy/v1`,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json'
    }
  };
};

// Cliente API para agenda
const createAgendaAPI = () => {
  const config = getAPIConfig();
  const api = axios.create(config);

  // Interceptor para manejo de errores
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      console.error('Error en API de Agenda:', error);
      
      if (error.response) {
        // Error de respuesta del servidor
        const errorMessage = error.response.data?.message || 'Error en el servidor';
        throw new Error(errorMessage);
      } else if (error.request) {
        // Error de red
        throw new Error('Error de conexión. Verifica tu conexión a internet.');
      } else {
        // Error de configuración
        throw new Error('Error en la configuración de la solicitud.');
      }
    }
  );

  return api;
};

// ===== FUNCIONES DE EVENTOS =====

/**
 * Obtener lista de eventos con filtros
 * @param {Object} filters - Filtros para eventos
 * @param {string} filters.search - Texto de búsqueda
 * @param {string} filters.category - Categoría del evento
 * @param {string} filters.status - Estado del evento
 * @param {string} filters.dateFrom - Fecha inicio (YYYY-MM-DD)
 * @param {string} filters.dateTo - Fecha fin (YYYY-MM-DD)
 * @param {number} filters.page - Página (default: 1)
 * @param {number} filters.limit - Límite por página (default: 50)
 * @returns {Promise<Object>} Lista de eventos
 */
export const getEvents = async (filters = {}) => {
  try {
    const api = createAgendaAPI();
    const params = new URLSearchParams();

    // Aplicar filtros
    if (filters.search) params.append('search', filters.search);
    if (filters.category) params.append('category', filters.category);
    if (filters.status) params.append('status', filters.status);
    if (filters.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters.dateTo) params.append('date_to', filters.dateTo);
    if (filters.visibility) params.append('visibility', filters.visibility);
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);

    const response = await api.get(`/agenda/events?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener eventos:', error);
    throw error;
  }
};

/**
 * Obtener evento por ID
 * @param {number|string} eventId - ID del evento
 * @returns {Promise<Object>} Evento
 */
export const getEvent = async (eventId) => {
  try {
    const api = createAgendaAPI();
    const response = await api.get(`/agenda/events/${eventId}`);
    return response.data;
  } catch (error) {
    console.error(`Error al obtener evento ${eventId}:`, error);
    throw error;
  }
};

/**
 * Crear nuevo evento
 * @param {Object} eventData - Datos del evento
 * @param {string} eventData.title - Título del evento
 * @param {string} eventData.description - Descripción
 * @param {string} eventData.start_date - Fecha/hora inicio (ISO)
 * @param {string} eventData.end_date - Fecha/hora fin (ISO)
 * @param {boolean} eventData.all_day - Es todo el día
 * @param {string} eventData.category - Categoría
 * @param {string} eventData.location - Ubicación
 * @param {string} eventData.status - Estado
 * @param {Array<string>} eventData.attendees - Lista de asistentes
 * @param {number} eventData.reminder - Recordatorio en minutos
 * @param {string} eventData.repeat - Patrón de repetición
 * @returns {Promise<Object>} Evento creado
 */
export const createEvent = async (eventData) => {
  try {
    const api = createAgendaAPI();
    
    // Validaciones básicas
    if (!eventData.title || !eventData.start_date || !eventData.end_date) {
      throw new Error('Título, fecha de inicio y fecha de fin son requeridos');
    }

    const response = await api.post('/agenda/events', eventData);
    return response.data;
  } catch (error) {
    console.error('Error al crear evento:', error);
    throw error;
  }
};

/**
 * Actualizar evento existente
 * @param {number|string} eventId - ID del evento
 * @param {Object} eventData - Datos actualizados del evento
 * @returns {Promise<Object>} Evento actualizado
 */
export const updateEvent = async (eventId, eventData) => {
  try {
    const api = createAgendaAPI();
    
    if (!eventId) {
      throw new Error('ID del evento es requerido');
    }

    const response = await api.put(`/agenda/events/${eventId}`, eventData);
    return response.data;
  } catch (error) {
    console.error(`Error al actualizar evento ${eventId}:`, error);
    throw error;
  }
};

/**
 * Eliminar evento
 * @param {number|string} eventId - ID del evento
 * @returns {Promise<Object>} Resultado de la eliminación
 */
export const deleteEvent = async (eventId) => {
  try {
    const api = createAgendaAPI();
    
    if (!eventId) {
      throw new Error('ID del evento es requerido');
    }

    const response = await api.delete(`/agenda/events/${eventId}`);
    return response.data;
  } catch (error) {
    console.error(`Error al eliminar evento ${eventId}:`, error);
    throw error;
  }
};

// ===== FUNCIONES DE CALENDARIO =====

/**
 * Obtener eventos de calendario para un usuario específico
 * @param {number|string} userId - ID del usuario
 * @param {Object} options - Opciones de consulta
 * @param {string} options.month - Mes (YYYY-MM)
 * @param {string} options.view - Vista (month, week, day)
 * @returns {Promise<Object>} Eventos del calendario
 */
export const getCalendarEvents = async (userId, options = {}) => {
  try {
    const api = createAgendaAPI();
    const params = new URLSearchParams();

    if (options.month) params.append('month', options.month);
    if (options.view) params.append('view', options.view);

    const response = await api.get(`/agenda/calendar/${userId}?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error(`Error al obtener calendario del usuario ${userId}:`, error);
    throw error;
  }
};

/**
 * Obtener estadísticas de eventos
 * @param {Object} filters - Filtros para estadísticas
 * @param {string} filters.period - Período (week, month, year)
 * @param {string} filters.dateFrom - Fecha inicio
 * @param {string} filters.dateTo - Fecha fin
 * @returns {Promise<Object>} Estadísticas de eventos
 */
export const getEventsStats = async (filters = {}) => {
  try {
    const api = createAgendaAPI();
    const params = new URLSearchParams();

    if (filters.period) params.append('period', filters.period);
    if (filters.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters.dateTo) params.append('date_to', filters.dateTo);

    const response = await api.get(`/agenda/stats?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas de eventos:', error);
    throw error;
  }
};

// ===== FUNCIONES AUXILIARES =====

/**
 * Buscar eventos por texto
 * @param {string} query - Texto de búsqueda
 * @param {Object} options - Opciones de búsqueda
 * @param {number} options.limit - Límite de resultados
 * @returns {Promise<Object>} Resultados de búsqueda
 */
export const searchEvents = async (query, options = {}) => {
  try {
    if (!query || query.trim().length < 2) {
      return { data: [] };
    }

    return await getEvents({
      search: query.trim(),
      limit: options.limit || 20
    });
  } catch (error) {
    console.error('Error en búsqueda de eventos:', error);
    throw error;
  }
};

/**
 * Obtener eventos próximos (siguientes 7 días)
 * @param {number} limit - Límite de eventos
 * @returns {Promise<Object>} Eventos próximos
 */
export const getUpcomingEvents = async (limit = 10) => {
  try {
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);

    return await getEvents({
      dateFrom: today.toISOString().split('T')[0],
      dateTo: nextWeek.toISOString().split('T')[0],
      status: 'scheduled',
      limit
    });
  } catch (error) {
    console.error('Error al obtener eventos próximos:', error);
    throw error;
  }
};

/**
 * Duplicar evento
 * @param {number|string} eventId - ID del evento a duplicar
 * @param {Object} modifications - Modificaciones al evento duplicado
 * @returns {Promise<Object>} Evento duplicado
 */
export const duplicateEvent = async (eventId, modifications = {}) => {
  try {
    // Obtener evento original
    const originalEvent = await getEvent(eventId);
    
    // Crear copia con modificaciones
    const duplicatedEvent = {
      ...originalEvent.data,
      title: `${originalEvent.data.title} (Copia)`,
      ...modifications
    };

    // Remover campos que no se deben duplicar
    delete duplicatedEvent.id;
    delete duplicatedEvent.created_at;
    delete duplicatedEvent.updated_at;

    return await createEvent(duplicatedEvent);
  } catch (error) {
    console.error(`Error al duplicar evento ${eventId}:`, error);
    throw error;
  }
};

/**
 * Exportar eventos a formato ICS (iCalendar)
 * @param {Object} filters - Filtros para exportación
 * @returns {Promise<Blob>} Archivo ICS
 */
export const exportEventsToICS = async (filters = {}) => {
  try {
    const api = createAgendaAPI();
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const response = await api.get(`/agenda/export/ics?${params.toString()}`, {
      responseType: 'blob'
    });

    return new Blob([response.data], { type: 'text/calendar' });
  } catch (error) {
    console.error('Error al exportar eventos:', error);
    throw error;
  }
};

/**
 * Verificar conflictos de horario
 * @param {Object} eventData - Datos del evento a verificar
 * @param {string} excludeEventId - ID del evento a excluir (para edición)
 * @returns {Promise<Object>} Conflictos encontrados
 */
export const checkEventConflicts = async (eventData, excludeEventId = null) => {
  try {
    const api = createAgendaAPI();
    const params = {
      start_date: eventData.start_date,
      end_date: eventData.end_date,
      location: eventData.location
    };

    if (excludeEventId) {
      params.exclude_id = excludeEventId;
    }

    const response = await api.post('/agenda/check-conflicts', params);
    return response.data;
  } catch (error) {
    console.error('Error al verificar conflictos:', error);
    throw error;
  }
};

// ===== CONFIGURACIÓN Y UTILIDADES =====

/**
 * Obtener configuración de la agenda
 * @returns {Promise<Object>} Configuración de la agenda
 */
export const getAgendaConfig = async () => {
  try {
    const api = createAgendaAPI();
    const response = await api.get('/agenda/config');
    return response.data;
  } catch (error) {
    console.error('Error al obtener configuración de agenda:', error);
    throw error;
  }
};

/**
 * Actualizar configuración de la agenda
 * @param {Object} config - Nueva configuración
 * @returns {Promise<Object>} Configuración actualizada
 */
export const updateAgendaConfig = async (config) => {
  try {
    const api = createAgendaAPI();
    const response = await api.put('/agenda/config', config);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar configuración de agenda:', error);
    throw error;
  }
};

// Exportar todas las funciones por defecto
export default {
  // Eventos
  getEvents,
  getEvent,
  createEvent,
  updateEvent,
  deleteEvent,
  
  // Calendario
  getCalendarEvents,
  getEventsStats,
  
  // Auxiliares
  searchEvents,
  getUpcomingEvents,
  duplicateEvent,
  exportEventsToICS,
  checkEventConflicts,
  
  // Configuración
  getAgendaConfig,
  updateAgendaConfig
};